import { formatStoredPhoneForDisplay } from "./phoneNumberSupport";

export function questionnaireResponseIdFrom(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const id = value.split("/").filter(Boolean).pop();
    return id || null;
  }
  if (typeof value === "object") {
    if (typeof value.reference === "string") return questionnaireResponseIdFrom(value.reference);
    if (typeof value.id === "string") return value.id;
  }
  return null;
}

export function questionnaireFromGetResponse(response) {
  const body = response?.data?.data?.item
    ? response.data.data
    : response?.data?.item
      ? response.data
      : response?.item
        ? response
        : null;
  if (!body?.item) return null;
  return body;
}

function canonicalPhone(callerNumber, callerNumberExt) {
  const raw = String(callerNumber || "");
  const embedded = raw.match(/ext\.?\s*(\d+)/i);
  const ext = String(callerNumberExt || embedded?.[1] || "").replace(/\D/g, "");
  let digits = raw.replace(/\D/g, "");
  if (ext && digits.endsWith(ext) && digits.length > ext.length) {
    digits = digits.slice(0, -ext.length);
  }
  return { digits, ext };
}

function displayPhone(callerNumber, callerNumberExt) {
  const { digits, ext } = canonicalPhone(callerNumber, callerNumberExt);
  const formatted = formatStoredPhoneForDisplay(digits);
  return ext ? `${formatted} ext. ${ext}` : formatted;
}

function phonesDiffer(current, callerNumber, callerNumberExt) {
  const booked = canonicalPhone(callerNumber, callerNumberExt);
  const existing = canonicalPhone(current, "");
  if (!booked.digits && !booked.ext) return false;
  return booked.digits !== existing.digits || booked.ext !== existing.ext;
}

function channelOptionScore(option, preferredChannel) {
  const text = String(option || "").trim().toLowerCase();
  if (preferredChannel === "phone") return text === "phone" ? 2 : 0;
  if (preferredChannel === "video/chat") {
    if (text.includes("video") && text.includes("chat")) return 3;
    if (text.includes("video") || text.includes("chat")) return 2;
  }
  return 0;
}

function withChannelAnswer(question, preferredChannel) {
  if (!Array.isArray(question.answer) || !preferredChannel) return question;
  const scores = question.answer.map((answer) => channelOptionScore(answer?.option, preferredChannel));
  const best = Math.max(0, ...scores);
  if (best === 0 || scores.filter((score) => score === best).length !== 1) return question;
  const winnerIndex = scores.indexOf(best);
  if (question.answer[winnerIndex]?.valueBoolean === true) return question;
  return {
    ...question,
    answer: question.answer.map((answer, index) => ({
      ...answer,
      valueBoolean: index === winnerIndex,
    })),
  };
}

export function withBookingIntakeAnswers(questionnaire, intakeQuestions) {
  if (!questionnaire || !Array.isArray(questionnaire.item) || !intakeQuestions) return questionnaire;
  const reason = typeof intakeQuestions.intakeReason === "string" ? intakeQuestions.intakeReason.trim() : "";
  const phone = typeof intakeQuestions.callerNumber === "string" ? intakeQuestions.callerNumber.trim() : "";
  const phoneExt = typeof intakeQuestions.callerNumberExt === "string" ? intakeQuestions.callerNumberExt.trim() : "";
  const channel = typeof intakeQuestions.preferredChannel === "string" ? intakeQuestions.preferredChannel.trim() : "";

  let changed = false;
  const item = questionnaire.item.map((question) => {
    if (question?.tag === "REASON" && reason) {
      const current = question.answer?.[0]?.valueString ?? "";
      if (current.trim() === reason) return question;
      changed = true;
      return {
        ...question,
        answer: [{ ...(question.answer?.[0] || {}), valueString: reason }],
      };
    }
    if (question?.tag === "TELEPHONE" && phone && phonesDiffer(question.answer?.[0]?.valueString, phone, phoneExt)) {
      changed = true;
      return {
        ...question,
        answer: [{ ...(question.answer?.[0] || {}), valueString: displayPhone(phone, phoneExt) }],
      };
    }
    if (question?.tag === "CHANNEL" && channel) {
      const next = withChannelAnswer(question, channel);
      if (next !== question) changed = true;
      return next;
    }
    return question;
  });

  return changed ? { ...questionnaire, item } : questionnaire;
}

export function questionnairesForEncounterReport({ responses, sourceQuestionnaire, intakeQuestions }) {
  const list = Array.isArray(responses) ? responses : [];
  if (!sourceQuestionnaire) return list;
  const source = withBookingIntakeAnswers(sourceQuestionnaire, intakeQuestions);
  const sourceId = source?.id;
  return [source, ...list.filter((item) => !sourceId || item?.id !== sourceId)];
}
